import { useEffect, useState } from 'react';

/**
 * =============================================================================
 * waitPhrases.js — the two waits, and what cycles during each
 * =============================================================================
 *
 * SHARED BY `SearchResultsPage` (the orrery) AND `ProblemCard` (the answer
 * slot). It cannot live in either: the page imports the panel, which imports
 * the card, so anything the card takes from the page is a cycle.
 *
 * ── TWO WAITS, TWO TREATMENTS ──────────────────────────────────────────────
 *
 * PHRASES ACCOMPANY A WAIT WITH NOTHING ELSE TO READ.
 *
 *   TRANSCRIPTION   the orrery, alone on the page. 30 to 90 seconds and not one
 *                   word of the student's problem is on screen yet. Phrases.
 *
 *   A TYPED QUESTION the card holds their own sentence and nothing else.
 *                   Phrases.
 *
 *   A WORKSHEET PROBLEM, after transcription — the interpretation is already
 *                   there to read, and sixteen cards each cycling their own
 *                   phrase would be chaos. A BAR, no phrases.
 *
 * That last one is why there are two schedules and two bar styles rather than
 * one of each.
 * =============================================================================
 */

export const WAIT_PHRASES = [
  'Consulting the math gods',
  'Ripping through a thousand years of proofs',
  'Waking Euclid',
  'Bribing a theorem',
  'Asking Pythagoras nicely',
  'Cornering the answer',
  'Making the numbers confess',
  'Untangling this one',
  'Arguing with a fraction',
  'Negotiating with x',
  'Rounding up the variables',
  'Consulting the ancient scrolls',
  'Interrogating a polynomial',
  'Summoning the quadratic formula',
  'Prying an answer out of this',
  'Wrestling a variable to the ground',
  'Convincing the denominator',
  'Rifling through Euclid\'s notes',
  'Checking with Archimedes',
  'Coaxing the equation open',
  'Reading the fine print on infinity',
  'Digging through the axioms',
  'Assembling the proof',
  'Cross-examining the coefficients',
  'Hunting for the pattern',
  'Persuading both sides to agree',
  'Setting the traps',
  'Consulting a very old textbook',
  'Chasing the solution downhill',
  'Bothering Newton about it',
  'Pinning down the unknown',
  'Unfolding the problem',
  'Turning the equation over',
  'Weighing both sides',
  'Following the thread',
  'Sifting the terms',
  'Waking the theorem up',
  'Borrowing Gauss\'s method',
  'Squeezing the answer out',
  'Unpicking the algebra',
  'Running it past Fermat',
  'Isolating the variable',
  'Lining up the steps',
  'Consulting the rulebook',
  'Tracking down the constant',
  'Tidying up the expression',
  'Testing a hunch',
  'Balancing the whole thing',
  'Backing out the answer',
  'Reverse-engineering the setup',
  'Bringing order to this',
  'Sorting the signal from the noise',
  'Working the problem',
  'Reading the shape of it',
  'Finding where it gives',
  'Prising the terms apart',
  'Chasing the logic through',
  'Following the numbers home',
  'Applying leverage',
  'Loosening the brackets',
  'Casting about for a method',
  'Rummaging in the toolbox',
  'Trying the obvious thing first',
  'Ruling out the wrong paths',
  'Circling the answer',
  'Closing in',
  'Taking the long way round',
  'Building it up from nothing',
  'Laying the groundwork',
  'Doing the tedious part',
  'Grinding through it',
  'Turning the handle',
  'Letting the algebra do the work',
  'Waiting for it to click',
  'Spotting the trick',
  'Seeing what falls out',
  'Simplifying ruthlessly',
  'Cancelling what we can',
  'Collecting the like terms',
  'Substituting and hoping',
  'Checking that actually works',
  'Making sure it holds',
  'Sanity-checking the result',
  'Retracing the steps',
  'Confirming nothing went wrong',
  'Second-guessing that last line',
  'Making it presentable',
  'Putting it in order',
  'Writing it out properly',
  'Finding the clearest route',
  'Choosing which way to explain it',
  'Deciding where to start',
  'Picking the right approach',
  'Weighing two methods',
  'Going with the cleaner one',
  'Trimming the working',
  'Cutting the unnecessary steps',
  'Making it make sense',
  'Putting it in plain terms',
  'Getting to the point'
];

/**
 * EVERY WAIT TICKS ON THE SAME BEAT.
 *
 * There used to be two schedules — [0, 5s, 12s, 25s, 45s, 70s] for
 * transcription and [0, 1.6s, 3.5s, 7s] for classification — with the gaps
 * widening as the wait went on. The idea was that a long wait needs fewer
 * changes. In practice it reads as broken: the line sits unchanged for twenty
 * seconds and the student concludes the thing has hung.
 *
 * A FIXED TWO SECONDS, always, on both waits. It also means every animation on
 * screen shares one beat rather than each drifting on its own timing.
 */
export const PHRASE_MS = 2000;

/**
 * THE BEAT. Every animation in the panel is this or a whole multiple of it, so
 * they land together instead of scattering.
 *
 *   0.5s  the phrase crossfade
 *   1s    the answer-slot bar filling
 *   2s    a band sweeping a track
 *   4s    the orrery turning
 *
 * Nothing is 1.4s or 3.4s or 2.6s any more. Those were chosen one at a time and
 * the result was four things moving at four unrelated rates.
 */
export const BEAT_MS = 1000;

function shuffled() {
  const a = WAIT_PHRASES.slice();
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

/**
 * Rotate a phrase every couple of seconds for as long as `active`.
 *
 * Shuffled once per wait and then cycled, so a student who asks twice does not
 * get the same openings in the same order and conclude the thing is canned.
 * Ninety-nine phrases at two seconds is over three minutes before one repeats,
 * and the longest wait observed was ninety-two seconds.
 */
export function usePhases(active, intervalMs = PHRASE_MS) {
  const [index, setIndex] = useState(0);
  const [list, setList] = useState(shuffled);

  useEffect(() => {
    if (!active) { setIndex(0); return undefined; }

    setList(shuffled());
    setIndex(0);

    const id = setInterval(() => setIndex((n) => n + 1), intervalMs);
    return () => clearInterval(id);
  }, [active, intervalMs]);

  return { phrase: list[index % list.length] || 'Working on it' };
}

export function estimateSeconds(file) {
  const mb = file && file.size ? file.size / (1024 * 1024) : 2;
  const raw = 28 + mb * 9;
  const clamped = Math.max(25, Math.min(95, raw));
  return Math.round(clamped / 5) * 5;
}
